/**
 * Global service for managing Zoom call functions outside of ZoomCallProvider.
 *
 * Why:
 *   ZoomCallProvider is only mounted when a call is active (lazy initialization)
 *   to avoid heavy re-renders. But we still need to call functions like
 *   callCRCDevice, cancelCallCRCDevice, handleEndVideoCall from other parts of the app.
 *
 * How:
 *   - ZoomCallProvider registers functions on mount and unregisters on unmount
 *   - Call the functions from anywhere using the exported functions from this service
 *   - Or use the Redux thunks from videoCallSlice
 *
 * Note:
 *   startVideoCall is NOT registered here. Instead, we use Redux pendingTask pattern:
 *   1. startVideoCallThunk stores task in Redux and sets isVideoCallOpen to true
 *   2. This mounts ZoomCallProvider which reads pendingTask and auto-initiates the call
 */
type CallCRCDeviceFn = (sipAddress: string) => Promise<void>;
type CancelCallCRCDeviceFn = () => Promise<void>;
type HandleEndVideoCallFn = () => Promise<void>;

let callCRCDeviceFn: CallCRCDeviceFn | null = null;
let cancelCallCRCDeviceFn: CancelCallCRCDeviceFn | null = null;
let handleEndVideoCallFn: HandleEndVideoCallFn | null = null;

// ========== callCRCDevice ==========

/**
 * Register the callCRCDevice function from ZoomCallProvider.
 * This should be called once when ZoomCallProvider mounts.
 */
export const registerCallCRCDevice = (fn: CallCRCDeviceFn) => {
  callCRCDeviceFn = fn;
};

/**
 * Unregister the callCRCDevice function.
 * This should be called when ZoomCallProvider unmounts.
 */
export const unregisterCallCRCDevice = () => {
  callCRCDeviceFn = null;
};

/**
 * Call the registered callCRCDevice function.
 * Returns false if no active call (provider not mounted).
 */
export const callCRCDevice = async (sipAddress: string): Promise<boolean> => {
  if (!callCRCDeviceFn) {
    console.info('callCRCDevice: No active call - cannot add interpreter');
    return false;
  }
  await callCRCDeviceFn(sipAddress);
  return true;
};

/**
 * Check if callCRCDevice is registered.
 */
export const isCallCRCDeviceRegistered = (): boolean => {
  return callCRCDeviceFn !== null;
};

// ========== cancelCallCRCDevice ==========

/**
 * Register the cancelCallCRCDevice function from ZoomCallProvider.
 * This should be called once when ZoomCallProvider mounts.
 */
export const registerCancelCallCRCDevice = (fn: CancelCallCRCDeviceFn) => {
  cancelCallCRCDeviceFn = fn;
};

/**
 * Unregister the cancelCallCRCDevice function.
 * This should be called when ZoomCallProvider unmounts.
 */
export const unregisterCancelCallCRCDevice = () => {
  cancelCallCRCDeviceFn = null;
};

/**
 * Call the registered cancelCallCRCDevice function.
 * Returns false if no active call (provider not mounted).
 */
export const cancelCallCRCDevice = async (): Promise<boolean> => {
  if (!cancelCallCRCDeviceFn) {
    console.info('cancelCallCRCDevice: No active call - nothing to cancel');
    return false;
  }
  await cancelCallCRCDeviceFn();
  return true;
};

/**
 * Check if cancelCallCRCDevice is registered.
 */
export const isCancelCallCRCDeviceRegistered = (): boolean => {
  return cancelCallCRCDeviceFn !== null;
};

// ========== handleEndVideoCall ==========

/**
 * Register the handleEndVideoCall function from ZoomCallProvider.
 * This should be called once when ZoomCallProvider mounts.
 */
export const registerHandleEndVideoCall = (fn: HandleEndVideoCallFn) => {
  handleEndVideoCallFn = fn;
};

/**
 * Unregister the handleEndVideoCall function.
 * This should be called when ZoomCallProvider unmounts.
 */
export const unregisterHandleEndVideoCall = () => {
  handleEndVideoCallFn = null;
};

/**
 * Call the registered handleEndVideoCall function.
 * Returns false if no active call (provider not mounted).
 * Safe to call even when no call is active - will silently return false.
 */
export const handleEndVideoCall = async (): Promise<boolean> => {
  if (!handleEndVideoCallFn) {
    // Don't log - this is expected when called during logout without active call
    return false;
  }
  await handleEndVideoCallFn();
  return true;
};

/**
 * Check if handleEndVideoCall is registered.
 */
export const isHandleEndVideoCallRegistered = (): boolean => {
  return handleEndVideoCallFn !== null;
};
